import type { ReactNode } from 'react';
import { useJsonModal, useSecurity } from '../../hooks';
import type { Security } from '../../types';
import { IconButton } from '../Button';
import type { BoxProps } from '../Core';
import { VStack } from '../Core';
import { CurrencyIcon } from '../CurrencyIcon';
import { FormControlSizes } from '../Form';
import { IconName } from '../Icons';
import { Text } from '../Text';
import { SymbolDisplayWrapper } from './styles';

export const SymbolDisplaySize = {
  Small: 0,
  Medium: 1,
  Default: 2,
} as const;
export type SymbolDisplaySize = (typeof SymbolDisplaySize)[keyof typeof SymbolDisplaySize];

export interface SymbolDisplayProps {
  symbol?: string;
  showDescription?: boolean;
  size?: SymbolDisplaySize;
  unifiedLiquidityIndicator?: ReactNode;
  showJSON?: boolean;
  suffix?: ReactNode;
}

export const SymbolDisplay = ({
  symbol,
  showDescription = true,
  size,
  unifiedLiquidityIndicator,
  showJSON = false,
  suffix,
  ...props
}: SymbolDisplayProps & BoxProps) => {
  const security = useSecurity(symbol);
  const { handleClickJson, jsonModal } = useJsonModal<Security>();

  return (
    <SymbolDisplayWrapper
      {...props}
      gap={size === SymbolDisplaySize.Small ? 'spacing4' : size === SymbolDisplaySize.Medium ? 'spacing6' : 'spacing8'}
    >
      {security?.BaseCurrency && (
        <CurrencyIcon
          currency={security?.BaseCurrency}
          size={size === SymbolDisplaySize.Small ? 12 : size === SymbolDisplaySize.Medium ? 16 : 24}
        />
      )}
      <VStack gap="spacing2" alignItems="flex-start" style={{ lineHeight: 1 }}>
        <Text color="colorTextImportant">
          {security?.DisplaySymbol ?? symbol}
          {unifiedLiquidityIndicator}
        </Text>
        {showDescription && (
          <Text size="fontSizeTiny" color="colorTextDefault">
            {security?.Description}
          </Text>
        )}
      </VStack>
      {suffix}
      {showJSON && (
        <IconButton
          icon={IconName.Braces}
          ghost={true}
          onClick={() => handleClickJson(security)}
          size={FormControlSizes.Small}
        />
      )}
      {jsonModal}
    </SymbolDisplayWrapper>
  );
};
